<template>
  <div class="modern-usage-container">
    <div class="flex items-center justify-between mb-5">
      <h2 class="text-xl font-bold text-gray-900 dark:text-white">{{ t('usage.title', 'Usage') }}</h2>
      <div class="h-8 w-8 rounded-full bg-amber-50 dark:bg-amber-900/40 flex items-center justify-center">
        <Icon icon="tabler:chart-bar" class="text-amber-600 dark:text-amber-300" width="18" />
      </div>
    </div>

    <div v-if="loading === true" class="space-y-5">
      <div v-for="n in 2" :key="n" class="animate-pulse">
        <div class="h-4 bg-gray-100 dark:bg-gray-800 rounded w-24 mb-3"></div>
        <div class="h-10 bg-gray-100 dark:bg-gray-800 rounded-xl w-full"></div>
      </div>
    </div>

    <div v-else class="space-y-5">
      <div class="usage-item">
        <div class="flex justify-between items-start mb-2">
          <div class="flex flex-col">
            <span class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{{ t('usage.messagesLabel', 'Chat Messages') }}</span>
            <span class="text-2xl font-bold text-gray-900 dark:text-white mt-1">
              {{ formatPrimaryAmount(usage.messages) }}
            </span>
            <span class="text-xs text-gray-500 dark:text-gray-400 mt-1">{{ getPrimaryLabel(usage.messages) }}</span>
          </div>
          <div class="text-right">
            <div class="text-xs text-gray-500 dark:text-gray-400">{{ t('usage.used', 'Used') }}: {{ formatMetricValue(usage.messages.totalUsed) }}</div>
            <div class="text-xs text-gray-500 dark:text-gray-400 mt-1">{{ t('usage.total', 'Total') }}: {{ formatMetricValue(usage.messages.totalCredits) }}</div>
          </div>
          <div v-if="usage.messages.remaining !== null" class="text-xs font-bold text-amber-700 dark:text-amber-200 bg-amber-100 dark:bg-amber-900/40 px-2 py-1 rounded-md">
            {{ calculatePercent(usage.messages.totalUsed, usage.messages.totalCredits) }}%
          </div>
        </div>
        <div class="h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
          <div 
            class="h-full bg-amber-600 dark:bg-amber-300 rounded-full" 
            :style="{ width: calculatePercent(usage.messages.totalUsed, usage.messages.totalCredits) + '%' }"
          ></div>
        </div>
      </div>

      <div class="usage-item">
        <div class="flex justify-between items-start mb-2">
          <div class="flex flex-col">
            <span class="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{{ t('usage.voiceLabel', 'Voice Minutes') }}</span>
            <span class="text-2xl font-bold text-gray-900 dark:text-white mt-1">
              {{ formatPrimaryAmount(usage.voiceMinutes) }}
            </span>
            <span class="text-xs text-gray-500 dark:text-gray-400 mt-1">{{ getPrimaryLabel(usage.voiceMinutes) }}</span>
          </div>
          <div class="text-right">
            <div class="text-xs text-gray-500 dark:text-gray-400">{{ t('usage.used', 'Used') }}: {{ formatMetricValue(usage.voiceMinutes.totalUsed) }}</div>
            <div class="text-xs text-gray-500 dark:text-gray-400 mt-1">{{ t('usage.total', 'Total') }}: {{ formatMetricValue(usage.voiceMinutes.totalCredits) }}</div>
          </div>
          <div v-if="usage.voiceMinutes.remaining !== null" class="text-xs font-bold text-amber-700 dark:text-amber-200 bg-amber-100 dark:bg-amber-900/40 px-2 py-1 rounded-md">
            {{ calculatePercent(usage.voiceMinutes.totalUsed, usage.voiceMinutes.totalCredits) }}%
          </div>
        </div>
        <div class="h-2 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
          <div 
            class="h-full bg-amber-600 dark:bg-amber-300 rounded-full" 
            :style="{ width: calculatePercent(usage.voiceMinutes.totalUsed, usage.voiceMinutes.totalCredits) + '%' }"
          ></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from '@/i18n.js';
import { Icon } from "@iconify/vue/dist/offline";
import axiosInstance from '@/api/axiosInstance';
import { globalState } from '@/globalState';

const { t } = useI18n();
const loading = ref(true);
const usage = ref({
  messages: { totalCredits: null, totalUsed: 0, remaining: null },
  voiceMinutes: { totalCredits: null, totalUsed: 0, remaining: null }
});

function formatNumber(val) {
  if (val === undefined || val === null) return '0';
  return Number(val).toLocaleString();
}

function createEmptyUsage() {
  return {
    messages: { totalCredits: null, totalUsed: 0, remaining: null },
    voiceMinutes: { totalCredits: null, totalUsed: 0, remaining: null }
  };
}

function normalizeUsagePayload(payload) {
  const normalized = createEmptyUsage();
  if (payload !== undefined && payload !== null) {
    if (payload.messages !== undefined && payload.messages !== null) {
      const messageSection = payload.messages;
      if (messageSection.totalCredits === null) {
        normalized.messages.totalCredits = null;
      } else if (Number.isFinite(Number(messageSection.totalCredits)) === true) {
        normalized.messages.totalCredits = Number(messageSection.totalCredits);
      }
      if (Number.isFinite(Number(messageSection.totalUsed)) === true) {
        normalized.messages.totalUsed = Number(messageSection.totalUsed);
      }
      if (messageSection.remaining === null) {
        normalized.messages.remaining = null;
      } else if (Number.isFinite(Number(messageSection.remaining)) === true) {
        normalized.messages.remaining = Number(messageSection.remaining);
      }
    }
    if (payload.voiceMinutes !== undefined && payload.voiceMinutes !== null) {
      const voiceSection = payload.voiceMinutes;
      if (voiceSection.totalCredits === null) {
        normalized.voiceMinutes.totalCredits = null;
      } else if (Number.isFinite(Number(voiceSection.totalCredits)) === true) {
        normalized.voiceMinutes.totalCredits = Number(voiceSection.totalCredits);
      }
      if (Number.isFinite(Number(voiceSection.totalUsed)) === true) {
        normalized.voiceMinutes.totalUsed = Number(voiceSection.totalUsed);
      }
      if (voiceSection.remaining === null) {
        normalized.voiceMinutes.remaining = null;
      } else if (Number.isFinite(Number(voiceSection.remaining)) === true) {
        normalized.voiceMinutes.remaining = Number(voiceSection.remaining);
      }
    }
  }
  return normalized;
}

function formatMetricValue(val) {
  if (val === null) return 'Unlimited';
  return formatNumber(val);
}

function formatPrimaryAmount(section) {
  if (section.remaining === null) {
    return formatMetricValue(section.totalCredits);
  }
  return formatMetricValue(section.remaining);
}

function getPrimaryLabel(section) {
  if (section.remaining === null) {
    return t('usage.total', 'Total');
  }
  return t('usage.remaining', 'Remaining');
}

function calculatePercent(used, total) {
  if (total === null || total === undefined) return 0;
  if (Number(total) === 0) return 100;
  const p = Math.round((Number(used) / Number(total)) * 100);
  return Math.min(Math.max(p, 0), 100);
}

async function loadUsage() {
  loading.value = true;
  try {
    const base = globalState.apiEndpoint !== undefined && globalState.apiEndpoint !== null ? globalState.apiEndpoint : '';
    const url = base !== '' ? `${base}/api/reports/organizationUsage` : 'reports/organizationUsage';
    const response = await axiosInstance.get(url);
    if (response.data !== undefined && response.data !== null) {
      usage.value = normalizeUsagePayload(response.data);
    }
  } catch (error) {
    console.error('Error loading usage:', error);
    usage.value = createEmptyUsage();
  } finally {
    loading.value = false;
  }
}

onMounted(() => {
  loadUsage();
});
</script>

<style scoped>
.modern-usage-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.usage-item {
  position: relative;
}

</style>







